import React, { useState } from "react";
import {
  Box,
  Card,
  Menu,
  Stack,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  Area,
  XAxis,
  AreaChart,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import styles from "./zeroTrustHealth.module.scss";
import {
  scoreSummary,
  aiSummary,
  recommendedActions,
  riskTrend,
  riskFactors,
} from "./zeroTrustHealth";
import { downloadPDF } from "../hipaa/HipaaDetail";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import FilterableTable from "../../shared/Tables/FilterableTable";

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tooltipCard}>
      <div className={styles.tooltipTitle}>{label}</div>
      <div className={styles.tooltipValue}>
        Posture Score: {payload[0].value}
      </div>
    </div>
  );
};

const ZeroTrustHealth = () => {
  const [periodLabel, setPeriodLabel] = useState("Last Year");
  const [anchorEl, setAnchorEl] = useState(null);

  const categories = React.useMemo(
    () => ["all", ...new Set(riskFactors.map((row) => row.category))],
    []
  );
  const statuses = React.useMemo(
    () => ["all", ...new Set(riskFactors.map((row) => row.verificationStatus))],
    []
  );
  const riskLevels = React.useMemo(
    () => ["all", ...new Set(riskFactors.map((row) => row.riskLevel))],
    []
  );

  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard", href: "/" },
            { label: "Zero Trust Health" },
          ]}
        />{" "}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportButton}
          onClick={() => downloadPDF("Zero-trust-health")}
        >
          Export Report
        </Button>
      </div>
      <Stack id="report" gap={2}>
        <Card className={styles.heroCard}>
          <div className={styles.heroLeft}>
            <div className={styles.iconBubble}>
              <ShieldOutlinedIcon />
            </div>
            <div>
              <div className={styles.heroLabel}>Zero Trust Health</div>
              <div className={styles.heroSub}>
                Comprehensive health check of identity, device posture, and
                micro-segmentation policies.
              </div>
            </div>
          </div>
          <div className={styles.heroRight}>
            <div className={styles.scoreValue}>{scoreSummary.score}%</div>
            <div className={styles.scoreStatus}>{scoreSummary.status}</div>
          </div>
        </Card>
        <Card className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <div className={styles.sectionTitleRow}>
              <InfoOutlinedIcon className={styles.sectionIcon} />
              <Typography className={styles.sectionTitle}>
                AI Generated Summary
              </Typography>
            </div>
            <div className={styles.summaryMeta}>
              Last Updated: {aiSummary.lastUpdated}
            </div>
          </div>

          <Box
            className={styles.summaryBlock}
            sx={{ border: "1px solid rgba(5, 117, 230, 1)" }}
          >
            <div className={styles.blockTitle}>Executive Summary</div>
            <div className={styles.blockBody}>{aiSummary.executive}</div>
          </Box>

          <div className={styles.summaryBlock}>
            <div className={styles.blockTitle}>Recommended Actions</div>
            <div className={styles.actionList}>
              {recommendedActions.map((action) => (
                <Card
                  key={action.title}
                  className={styles.actionCard}
                  sx={{
                    borderLeft: `6px solid ${action.borderColor}`,
                  }}
                >
                  <div className={styles.actionHeader}>
                    <span
                      className={`${styles.actionPriority} ${
                        styles[action.priorityTone]
                      }`}
                    >
                      {action.priority}
                    </span>
                    <span className={styles.actionCve}>{action.cve}</span>
                  </div>
                  <div className={styles.actionTitle}>{action.title}</div>
                  <div className={styles.actionDesc}>{action.description}</div>
                </Card>
              ))}
            </div>
          </div>

          <div className={styles.summaryFootnote}>
            <span>Analysis Confidence: {aiSummary.confidence}</span>
            <span>Methodology: {aiSummary.methodology}</span>
          </div>
        </Card>
        <Card className={styles.trendCard}>
          <div className={styles.trendHeader}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Zero Trust Posture
            </Typography>
            <div>
              <Button
                variant="outlined"
                size="small"
                className={styles.filterButton}
                endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                {periodLabel}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {["Last 30 Days", "Last 6 Months", "Last Year"].map((label) => (
                  <MenuItem
                    key={label}
                    selected={label === periodLabel}
                    onClick={() => {
                      setPeriodLabel(label);
                      setAnchorEl(null);
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
          <div className={styles.trendChart}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={riskTrend}
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.5} />
                    <stop
                      offset="100%"
                      stopColor="#f59e0b"
                      stopOpacity={0.08}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#cbd5e1", fontSize: 12 }}
                  axisLine={{ stroke: "#1e293b" }}
                  tickLine={{ stroke: "#1e293b" }}
                />
                <RechartsTooltip
                  content={<TrendTooltip />}
                  cursor={{ stroke: "#fbbf24", strokeDasharray: "4 4" }}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#trendFill)"
                  dot={{
                    r: 5,
                    fill: "#fbbf24",
                    stroke: "#0f172a",
                    strokeWidth: 1.5,
                  }}
                  activeDot={{ r: 6, fill: "#f59e0b" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <FilterableTable
          title="Zero Trust Compliance Factors"
          data={riskFactors}
          columns={[
            {
              id: "id",
              label: "User/Device ID",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <div className={styles.assetCell}>
                  <div className={styles.assetName}>{row.id}</div>
                </div>
              ),
            },
            {
              id: "category",
              label: "Category",
              headClassName: styles.tableHead,
            },
            {
              id: "verificationStatus",
              label: "Verification Status",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span
                  className={
                    row.verificationStatus === "Verified"
                      ? styles.statusVerified
                      : row.verificationStatus === "Failed"
                      ? styles.statusFailed
                      : styles.statusPending
                  }
                >
                  {row.verificationStatus}
                </span>
              ),
            },
            {
              id: "postureCheck",
              label: "Posture Check",
              headClassName: styles.tableHead,
            },
            {
              id: "riskLevel",
              label: "Risk Level",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span
                  className={
                    row.riskLevel === "High"
                      ? styles.riskHigh
                      : row.riskLevel === "Medium"
                      ? styles.riskMedium
                      : styles.riskLow
                  }
                >
                  {row.riskLevel}
                </span>
              ),
            },
          ]}
          searchKeys={["id"]}
          searchPlaceholder="Search by User or Device ID"
          filters={[
            {
              id: "riskLevel",
              label: "Select Risk",
              options: riskLevels,
              valueAccessor: (row) => row.riskLevel,
              selectSx: {
                minWidth: 180,
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 1)",
              },
              formatOption: (option) => (option === "all" ? "All" : option),
            },
            {
              id: "category",
              label: "Category",
              options: categories,
              valueAccessor: (row) => row.category,
              selectSx: {
                minWidth: 180,
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 1)",
              },
              formatOption: (option) =>
                option === "all" ? "All Categories" : option,
            },
            {
              id: "verificationStatus",
              label: "Status",
              options: statuses,
              valueAccessor: (row) => row.verificationStatus,
              selectSx: {
                minWidth: 180,
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 1)",
              },
              formatOption: (option) =>
                option === "all" ? "All Statuses" : option,
            },
          ]}
          emptyMessage="No compliance factors match the selected filters."
          rowsPerPageOptions={[5, 10, 25]}
          classNames={{
            card: styles.tableCard,
            tableHeader: styles.tableHeader,
            tableHeaderActions: styles.tableHeaderActions,
            sectionTitle: styles.sectionTitle,
            searchField: styles.searchField,
            filterToggle: styles.filterToggle,
            filterRow: styles.filterRow,
            filterGroup: styles.filterGroup,
            filterLabel: styles.filterLabel,
            filterSelect: styles.filterSelect,
            clearFilters: styles.clearFilters,
            tableContainer: styles.tableContainer,
            tableHead: styles.tableHead,
            tablePagination: styles.tablePagination,
            emptyState: styles.emptyState,
          }}
          showFilterToggle
          defaultShowFilters
        />
      </Stack>
    </Box>
  );
};

export default ZeroTrustHealth;
